import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { OFFICIAL_TAG } from '../../data/officialTag';
import {
  fetchAdminDevelopers,
  fetchAdminUsage,
  updateDeveloper,
  fetchAdminAnimations,
  deleteAdminAnimation,
  updateAdminAnimation,
  fetchAdminUsers,
  banUser,
  unbanUser,
  updateUserRoles,
  fetchAdminStats,
  fetchAdminAttention,
  fetchAdminTags,
  createAdminTag,
  deleteAdminTag,
  updateAdminTag,
  fetchAdminCategories,
  createAdminCategory,
  deleteAdminCategory,
  updateAdminCategory,
  fetchAdminVpmVersions,
  updateAdminVpmVersion,
  deleteAdminVpmVersion,
  fetchAdminContacts,
  updateAdminContact,
  fetchAdminReports,
  updateAdminReport,
  fetchAdminSimilarityFlags,
  updateAdminSimilarityFlag,
  fetchAdminAuditLogs,
  startAdminPoseServer,
  stopAdminPoseServer,
  setAdminPoseAccepting,
  fetchAdminAppListings,
  updateAppListing,
  type DeveloperInfo,
  type DeveloperStatus,
  type UsageRow,
  type AdminAnimation,
  type AdminUser,
  type AdminStats,
  type AdminAttention,
  type AdminTag,
  type AdminCategory,
  type AdminVpmVersion,
  type AdminContact,
  type ContactCategory,
  type AdminReport,
  type ReportCategory,
  type AdminSimilarityFlag,
  type AdminAuditLog,
  type AppListingInfo,
  type AppListingStatus,
} from '../../api/developers';
import { fetchPoseServerHealth } from '../../api/client';
import { ROLES, ROLE_META, type Role } from '../../api/roles';
import { useAuth } from '../../auth/AuthContext';
import { displayNameFallback, UserAvatarChip } from '../../lib/userDisplay';
import Spinner from '../../components/Spinner';
import { usePagination, Pagination } from '../../components/SimplePagination';

type Tab =
  | 'overview'
  | 'developers'
  | 'listings'
  | 'usage'
  | 'animations'
  | 'users'
  | 'tags'
  | 'categories'
  | 'vpm'
  | 'reports'
  | 'similarity'
  | 'contacts'
  | 'audit'
  | 'pose';

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<DeveloperStatus, string> = {
  pending: '審査中',
  approved: '承認済み',
  rejected: '却下',
  suspended: '停止中',
};

const STATUS_BADGE_CLASS: Record<DeveloperStatus, string> = {
  pending: 'badge badge--pending',
  approved: 'badge badge--approved',
  rejected: 'badge badge--rejected',
  suspended: 'badge badge--suspended',
};

function StatusBadge({ status }: { status: DeveloperStatus }) {
  return <span className={STATUS_BADGE_CLASS[status]}>{STATUS_LABEL[status]}</span>;
}

/** タブボタンの右肩に表示する「要対応」件数バッジ。0件（または未取得）なら何も表示しない。 */
function TabCountBadge({ count }: { count: number }) {
  if (!count) return null;
  return <span className="badge badge--pending">{count}</span>;
}

function DeveloperRow({
  dev,
  onChanged,
}: {
  dev: DeveloperInfo;
  onChanged: () => void;
}) {
  const [quota, setQuota] = useState(dev.dailyQuota);
  const [monthlyQuota, setMonthlyQuota] = useState(dev.monthlyQuota);
  const [busy, setBusy] = useState(false);

  const run = async (patch: { status?: DeveloperStatus; dailyQuota?: number; monthlyQuota?: number }) => {
    setBusy(true);
    try {
      await updateDeveloper(dev.sub, patch);
      onChanged();
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td>{new Date(dev.createdAt).toLocaleString('ja-JP')}</td>
      <td>{dev.name}</td>
      <td>{dev.appName || '—'}</td>
      <td>{dev.purpose || '—'}</td>
      <td>
        <StatusBadge status={dev.status} />
      </td>
      <td>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="number"
            min={0}
            value={quota}
            onChange={(e) => setQuota(Number(e.target.value))}
            style={{ width: 90 }}
          />
          <button
            className="btn"
            disabled={busy || quota === dev.dailyQuota}
            onClick={() => run({ dailyQuota: quota })}
          >
            更新
          </button>
        </div>
      </td>
      <td>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="number"
            min={50000}
            step={10000}
            value={monthlyQuota}
            onChange={(e) => setMonthlyQuota(Number(e.target.value))}
            style={{ width: 100 }}
          />
          <button
            className="btn"
            disabled={busy || monthlyQuota === dev.monthlyQuota}
            onClick={() => run({ monthlyQuota })}
          >
            更新
          </button>
        </div>
      </td>
      <td>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {dev.status === 'pending' && (
            <>
              <button
                className="btn btn--primary"
                disabled={busy}
                onClick={() => run({ status: 'approved' })}
              >
                承認
              </button>
              <button className="btn" disabled={busy} onClick={() => run({ status: 'rejected' })}>
                却下
              </button>
            </>
          )}
          {dev.status === 'approved' && (
            <button className="btn" disabled={busy} onClick={() => run({ status: 'suspended' })}>
              停止
            </button>
          )}
          {(dev.status === 'suspended' || dev.status === 'rejected') && (
            <button
              className="btn btn--primary"
              disabled={busy}
              onClick={() => run({ status: 'approved' })}
            >
              承認（再開）
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function DevelopersTab() {
  const [developers, setDevelopers] = useState<DeveloperInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { page, setPage, totalPages, pageItems } = usePagination(developers, PAGE_SIZE);

  const load = () => {
    setLoading(true);
    fetchAdminDevelopers()
      .then(setDevelopers)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <Spinner />;
  if (error) return <p className="viewer__error">{error}</p>;
  if (developers.length === 0) return <p>開発者申請はまだありません。</p>;

  return (
    <>
      <div className="notice" style={{ marginBottom: 12 }}>
        ※ステータス変更時、送信元メール設定済みの環境では申請者へ通知メールが送られます。
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>申請日時</th>
            <th>名前</th>
            <th>アプリ名</th>
            <th>利用目的</th>
            <th>ステータス</th>
            <th>日次クォータ</th>
            <th>月間クォータ</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((dev) => (
            <DeveloperRow key={dev.sub} dev={dev} onChanged={load} />
          ))}
        </tbody>
      </table>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </>
  );
}

const APP_LISTING_STATUS_LABEL: Record<AppListingStatus, string> = {
  pending: '審査中',
  approved: '掲載中',
  rejected: '却下',
  suspended: '停止中',
};

function AppListingStatusBadge({ status }: { status: AppListingStatus }) {
  return <span className={STATUS_BADGE_CLASS[status]}>{APP_LISTING_STATUS_LABEL[status]}</span>;
}

function AppListingRow({
  listing,
  onChanged,
}: {
  listing: AppListingInfo;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const run = async (status: AppListingStatus) => {
    setBusy(true);
    try {
      await updateAppListing(listing.appId, { status });
      onChanged();
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className={`app-icon app-icon--md ${!listing.iconUrl ? 'app-icon--tone1' : ''}`}>
            {listing.iconUrl ? <img src={listing.iconUrl} alt="" /> : listing.name.charAt(0).toUpperCase()}
          </span>
          <span>{listing.name}</span>
        </div>
      </td>
      <td>
        {listing.developerAppName ?? '—'}
        <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>{listing.sub}</div>
      </td>
      <td>
        <a href={listing.url} target="_blank" rel="noopener noreferrer">
          {listing.url}
        </a>
      </td>
      <td>
        <AppListingStatusBadge status={listing.status} />
      </td>
      <td>{new Date(listing.createdAt).toLocaleString('ja-JP')}</td>
      <td>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {listing.status === 'pending' && (
            <>
              <button className="btn btn--primary" disabled={busy} onClick={() => run('approved')}>
                承認
              </button>
              <button className="btn" disabled={busy} onClick={() => run('rejected')}>
                却下
              </button>
            </>
          )}
          {listing.status === 'approved' && (
            <button className="btn" disabled={busy} onClick={() => run('suspended')}>
              停止
            </button>
          )}
          {listing.status === 'suspended' && (
            <button className="btn btn--primary" disabled={busy} onClick={() => run('approved')}>
              承認（再開）
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function ListingsTab() {
  const [listings, setListings] = useState<AppListingInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { page, setPage, totalPages, pageItems } = usePagination(listings, PAGE_SIZE);

  const load = () => {
    setLoading(true);
    fetchAdminAppListings()
      .then(setListings)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <Spinner />;
  if (error) return <p className="viewer__error">{error}</p>;
  if (listings.length === 0) return <p>掲載申請はまだありません。</p>;

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>アプリ</th>
            <th>開発者</th>
            <th>URL</th>
            <th>状態</th>
            <th>申請日時</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((l) => (
            <AppListingRow key={l.appId} listing={l} onChanged={load} />
          ))}
        </tbody>
      </table>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </>
  );
}

function UsageTab() {
  const [data, setData] = useState<{ developers: DeveloperInfo[]; usage: UsageRow[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    fetchAdminUsage(14)
      .then(setData)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const developers = data?.developers ?? [];
  const usage = data?.usage ?? [];
  const devPagination = usePagination(developers, PAGE_SIZE);
  const usagePagination = usePagination(usage, PAGE_SIZE);

  if (loading) return <Spinner />;
  if (error) return <p className="viewer__error">{error}</p>;
  if (!data || data.developers.length === 0) return <p>まだ利用がありません。</p>;

  return (
    <>
      <h3>開発者ごとの集計（直近14日）</h3>
      <div style={{ marginBottom: 24 }}>
        <table className="table">
          <thead>
            <tr>
              <th>名前</th>
              <th>アプリ名</th>
              <th>合計リクエスト</th>
              <th>合計エラー</th>
              <th>合計429</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {devPagination.pageItems.map((dev) => {
              const rows = usage.filter((u) => u.sub === dev.sub);
              const totalRequests = rows.reduce((a, r) => a + r.requests, 0);
              const totalErrors = rows.reduce((a, r) => a + r.errors, 0);
              const totalRateLimited = rows.reduce((a, r) => a + r.rateLimited, 0);
              return (
                <tr key={dev.sub}>
                  <td>{dev.name}</td>
                  <td>{dev.appName || '—'}</td>
                  <td>{totalRequests}</td>
                  <td>{totalErrors}</td>
                  <td>{totalRateLimited}</td>
                  <td>
                    {totalRateLimited > 0 && <span className="badge badge--warning">要注意</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination
          page={devPagination.page}
          totalPages={devPagination.totalPages}
          onChange={devPagination.setPage}
        />
      </div>

      <h3>日別明細</h3>
      <table className="table">
        <thead>
          <tr>
            <th>日付</th>
            <th>開発者</th>
            <th>リクエスト</th>
            <th>エラー</th>
            <th>429</th>
          </tr>
        </thead>
        <tbody>
          {usagePagination.pageItems.map((u) => {
            const dev = developers.find((d) => d.sub === u.sub);
            return (
              <tr key={`${u.sub}-${u.date}`}>
                <td>{u.date}</td>
                <td>{dev?.name ?? u.sub}</td>
                <td>{u.requests}</td>
                <td>{u.errors}</td>
                <td>{u.rateLimited}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        page={usagePagination.page}
        totalPages={usagePagination.totalPages}
        onChange={usagePagination.setPage}
      />
    </>
  );
}

function OverviewTab({
  attention,
  onNavigate,
}: {
  attention: AdminAttention | null;
  onNavigate: (tab: Tab) => void;
}) {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    fetchAdminStats()
      .then(setStats)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (error) return <p className="viewer__error">{error}</p>;
  if (!stats) return null;

  // ユーザー数はページング上限まで数えた概数の場合があるため、その場合は「≧」を付ける。
  const userFmt = (n: number) => (stats.users.truncated ? `≧${n}` : `${n}`);

  return (
    <div className="admin-stats">
      {attention &&
        (attention.pendingDevelopers > 0 ||
          attention.openReports > 0 ||
          attention.openContacts > 0 ||
          attention.openSimilarityFlags > 0) && (
        <div className="admin-stats__group">
          <h3>要対応</h3>
          <div className="admin-stats__cards">
            {attention.pendingDevelopers > 0 && (
              <button
                type="button"
                className="admin-stats__card admin-stats__card--attention"
                onClick={() => onNavigate('developers')}
              >
                <div className="admin-stats__card-label">審査中の開発者申請</div>
                <div className="admin-stats__card-value">{attention.pendingDevelopers}</div>
              </button>
            )}
            {attention.openReports > 0 && (
              <button
                type="button"
                className="admin-stats__card admin-stats__card--attention"
                onClick={() => onNavigate('reports')}
              >
                <div className="admin-stats__card-label">未対応の通報</div>
                <div className="admin-stats__card-value">{attention.openReports}</div>
              </button>
            )}
            {attention.openContacts > 0 && (
              <button
                type="button"
                className="admin-stats__card admin-stats__card--attention"
                onClick={() => onNavigate('contacts')}
              >
                <div className="admin-stats__card-label">未対応の問い合わせ</div>
                <div className="admin-stats__card-value">{attention.openContacts}</div>
              </button>
            )}
            {attention.openSimilarityFlags > 0 && (
              <button
                type="button"
                className="admin-stats__card admin-stats__card--attention"
                onClick={() => onNavigate('similarity')}
              >
                <div className="admin-stats__card-label">類似検出</div>
                <div className="admin-stats__card-value">{attention.openSimilarityFlags}</div>
              </button>
            )}
          </div>
        </div>
      )}

      <div className="admin-stats__group">
        <h3>アニメーション</h3>
        <div className="admin-stats__cards">
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">合計</div>
            <div className="admin-stats__card-value">{stats.animations.total}</div>
          </div>
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">公開</div>
            <div className="admin-stats__card-value">{stats.animations.public}</div>
          </div>
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">非公開</div>
            <div className="admin-stats__card-value">{stats.animations.private}</div>
          </div>
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">総再生</div>
            <div className="admin-stats__card-value">{stats.animations.totalPlays}</div>
          </div>
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">総DL</div>
            <div className="admin-stats__card-value">{stats.animations.totalDownloads}</div>
          </div>
        </div>
      </div>

      <div className="admin-stats__group">
        <h3>開発者</h3>
        <div className="admin-stats__cards">
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">合計</div>
            <div className="admin-stats__card-value">{stats.developers.total}</div>
          </div>
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">承認済み</div>
            <div className="admin-stats__card-value">{stats.developers.approved}</div>
          </div>
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">審査中</div>
            <div className="admin-stats__card-value">{stats.developers.pending}</div>
          </div>
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">停止</div>
            <div className="admin-stats__card-value">{stats.developers.suspended}</div>
          </div>
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">却下</div>
            <div className="admin-stats__card-value">{stats.developers.rejected}</div>
          </div>
        </div>
      </div>

      <div className="admin-stats__group">
        <h3>ユーザー</h3>
        <div className="admin-stats__cards">
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">合計</div>
            <div className="admin-stats__card-value">{userFmt(stats.users.total)}</div>
          </div>
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">有効</div>
            <div className="admin-stats__card-value">{userFmt(stats.users.enabled)}</div>
          </div>
          <div className="admin-stats__card">
            <div className="admin-stats__card-label">無効</div>
            <div className="admin-stats__card-value">{userFmt(stats.users.disabled)}</div>
          </div>
        </div>
        {stats.users.truncated && (
          <p className="admin-stats__note">
            ユーザー数が多いため件数はページング上限までの概数です（≧は「この値以上」を表します）。
          </p>
        )}
      </div>
    </div>
  );
}

type VisibilityFilter = 'all' | 'public' | 'private';

/** ユーザー詳細から「投稿を絞り込む」で渡すオーナー絞り込み条件（sub + 表示用ラベル）。 */
export interface AnimationsOwnerFilter {
  sub: string;
  label: string;
}

function AnimationsTab({
  ownerFilter,
  onClearOwnerFilter,
}: {
  ownerFilter: AnimationsOwnerFilter | null;
  onClearOwnerFilter: () => void;
}) {
  const [animations, setAnimations] = useState<AdminAnimation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('all');
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchAdminAnimations()
      .then(setAnimations)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const needle = filter.trim().toLowerCase();
  const filtered = animations.filter((a) => {
    const matchesText =
      !needle || a.title.toLowerCase().includes(needle) || a.tags.some((t) => t.toLowerCase().includes(needle));
    const matchesVisibility = visibilityFilter === 'all' || a.visibility === visibilityFilter;
    const matchesOwner = !ownerFilter || a.ownerId === ownerFilter.sub;
    return matchesText && matchesVisibility && matchesOwner;
  });

  const { page, setPage, totalPages, pageItems } = usePagination(filtered, PAGE_SIZE);

  // 絞り込み条件が変わったら、ページ範囲外になる前に明示的に1ページ目へ戻す。
  useEffect(() => {
    setPage(1);
  }, [filter, visibilityFilter, ownerFilter, setPage]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('本当に削除しますか？（S3 実体も削除されます）')) return;
    const blockFingerprint = window.confirm(
      '削除前にこのモーションのフィンガープリントをブロックリストへ登録しますか？\n' +
        '（権利侵害による削除の場合に推奨。以降、同一・類似モーションの再投稿を自動検出できます）\n\n' +
        'OK: 登録して削除 / キャンセル: 登録せず削除',
    );
    setBusyId(id);
    try {
      await deleteAdminAnimation(id, { blockFingerprint });
      setAnimations((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleVisibility = async (a: AdminAnimation) => {
    const next = a.visibility === 'public' ? 'private' : 'public';
    const confirmMessage = next === 'private' ? '非公開にしますか？' : '公開にしますか？';
    if (!window.confirm(confirmMessage)) return;
    setBusyId(a.id);
    try {
      const updated = await updateAdminAnimation(a.id, { visibility: next });
      setAnimations((prev) => prev.map((x) => (x.id === a.id ? { ...x, visibility: updated.visibility } : x)));
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <p className="viewer__error">{error}</p>;

  return (
    <>
      {ownerFilter && (
        <div style={{ marginBottom: 12 }}>
          <span className="badge">
            オーナー: {ownerFilter.label} で絞り込み中{' '}
            <button
              type="button"
              onClick={onClearOwnerFilter}
              aria-label="オーナー絞り込みを解除"
              style={{
                marginLeft: 6,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: 'inherit',
                font: 'inherit',
                padding: 0,
              }}
            >
              ×
            </button>
          </span>
        </div>
      )}
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="タイトル・タグで絞り込み"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ width: 280 }}
        />
        <select value={visibilityFilter} onChange={(e) => setVisibilityFilter(e.target.value as VisibilityFilter)}>
          <option value="all">すべて</option>
          <option value="public">公開のみ</option>
          <option value="private">非公開のみ</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        <p>該当するアニメーションはありません。</p>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>作成日</th>
                <th>サムネ</th>
                <th>タイトル</th>
                <th>オーナー</th>
                <th>再生</th>
                <th>DL</th>
                <th>公開</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((a) => (
                <tr key={a.id}>
                  <td>{new Date(a.createdAt).toLocaleString('ja-JP')}</td>
                  <td>
                    {a.thumbnailUrl && (
                      <img
                        src={a.thumbnailUrl}
                        alt=""
                        style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }}
                      />
                    )}
                  </td>
                  <td>
                    <Link to={`/animations/${a.id}`}>{a.title}</Link>
                  </td>
                  <td>
                    <UserAvatarChip
                      iconUrl={a.ownerIconUrl}
                      name={a.ownerNickname ?? displayNameFallback(a.ownerEmail, a.ownerId)}
                    />
                  </td>
                  <td>{a.playCount ?? 0}</td>
                  <td>{a.downloadCount ?? 0}</td>
                  <td>
                    <span className={a.visibility === 'private' ? 'badge badge--private' : 'badge badge--approved'}>
                      {a.visibility === 'private' ? '非公開' : '公開'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn" disabled={busyId === a.id} onClick={() => handleToggleVisibility(a)}>
                        {a.visibility === 'private' ? '公開にする' : '非公開にする'}
                      </button>
                      <button className="btn" disabled={busyId === a.id} onClick={() => handleDelete(a.id)}>
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </>
  );
}

/** ロールバッジの並び（roles.map で表示）。旧データで roles が無ければ何も出さない。 */
function RoleBadges({ roles }: { roles: Role[] }) {
  if (!roles || roles.length === 0) return null;
  return (
    <>
      {roles.map((r) => (
        <span key={r} className={`badge ${ROLE_META[r].badgeClass}`}>
          {ROLE_META[r].label}
        </span>
      ))}
    </>
  );
}

/**
 * ユーザー詳細モーダル（API 利用状況 + ロール編集）。
 * Escape/背景クリックで閉じる・body スクロールロック等の作法は他のモーダルと揃えている。
 */
function UserDetailsModal({
  open,
  onClose,
  user,
  developer,
  monthlyUsage,
  currentUserSub,
  onRolesUpdated,
  onFilterAnimations,
  onBan,
  onUnban,
  banBusy,
}: {
  open: boolean;
  onClose: () => void;
  user: AdminUser;
  developer?: DeveloperInfo;
  /** 当月の合計リクエスト数（直近30日の利用状況集計から算出）。未取得なら undefined。 */
  monthlyUsage?: number;
  currentUserSub?: string;
  onRolesUpdated: (updated: { sub: string; roles: Role[] }) => void;
  /** 「このユーザーの投稿を管理画面で絞り込む」押下時に呼ばれる（アニメーションタブへ遷移させる）。 */
  onFilterAnimations: (sub: string, label: string) => void;
  onBan: (sub: string) => void | Promise<void>;
  onUnban: (sub: string) => void | Promise<void>;
  banBusy: boolean;
}) {
  const [draftRoles, setDraftRoles] = useState<Role[]>(user.roles.length > 0 ? user.roles : ['user']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const isSelf = !!currentUserSub && user.sub === currentUserSub;

  // 開き直し（別ユーザーの詳細を開いた場合も含む）のたびに draft をリセットする。
  useEffect(() => {
    setDraftRoles(user.roles.length > 0 ? user.roles : ['user']);
    setError('');
  }, [user]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    closeBtnRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const dirty =
    draftRoles.length !== user.roles.length || draftRoles.some((r) => !user.roles.includes(r));
  const overQuota = developer && monthlyUsage !== undefined && monthlyUsage > developer.monthlyQuota;

  const toggleRole = (role: Role) => {
    setDraftRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const updated = await updateUserRoles(user.sub, draftRoles);
      onRolesUpdated(updated);
      onClose();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="user-details-title">
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 id="user-details-title" className="modal__title">
            <UserAvatarChip iconUrl={user.iconUrl} name={user.nickname ?? displayNameFallback(user.email, user.sub)} />
            <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 14, color: 'var(--color-muted)' }}>
              {user.email ?? '—'}
            </span>
          </h3>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="閉じる"
            ref={closeBtnRef}
          >
            ×
          </button>
        </div>

        <section style={{ marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 8px' }}>基本情報</h4>
          <dl className="legal__dl">
            <dt>sub</dt>
            <dd>
              <code className="code-inline">{user.sub}</code>
            </dd>
            <dt>Cognito ステータス</dt>
            <dd>{user.status || '—'}</dd>
            <dt>有効</dt>
            <dd>
              <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                {user.enabled ? (
                  <span className="badge badge--approved">有効</span>
                ) : (
                  <span className="badge badge--rejected">無効</span>
                )}
                {user.enabled ? (
                  <button
                    type="button"
                    className="btn"
                    disabled={banBusy || isSelf}
                    title={isSelf ? '自分自身は無効化できません' : undefined}
                    onClick={() => onBan(user.sub)}
                  >
                    バン
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn"
                    disabled={banBusy || isSelf}
                    title={isSelf ? '自分自身は無効化できません' : undefined}
                    onClick={() => onUnban(user.sub)}
                  >
                    バン解除
                  </button>
                )}
              </div>
            </dd>
            <dt>作成日</dt>
            <dd>{user.createdAt ? new Date(user.createdAt).toLocaleString('ja-JP') : '—'}</dd>
            {!user.enabled && (
              <>
                <dt>BAN理由</dt>
                <dd>{user.banReason || '（理由記載なし）'}</dd>
              </>
            )}
          </dl>
        </section>

        <section style={{ marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 8px' }}>投稿</h4>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link className="btn" to={`/users/${user.sub}`} onClick={onClose}>
              このユーザーの公開ページを見る
            </Link>
            <button
              type="button"
              className="btn"
              onClick={() => {
                onFilterAnimations(user.sub, user.nickname ?? displayNameFallback(user.email, user.sub));
                onClose();
              }}
            >
              このユーザーの投稿を管理画面で絞り込む
            </button>
          </div>
        </section>

        <section style={{ marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 8px' }}>API 利用状況</h4>
          {developer ? (
            <>
              <p>
                開発者ステータス: <StatusBadge status={developer.status} />
              </p>
              <div className="plan-usage">
                <div className="plan-usage__row">
                  <span>当月の使用量（直近30日集計）</span>
                  <span className={`plan-usage__count${overQuota ? ' plan-usage__count--over' : ''}`}>
                    {monthlyUsage === undefined ? <Spinner inline size="sm" /> : `${monthlyUsage} / ${developer.monthlyQuota}`}
                  </span>
                </div>
                {monthlyUsage !== undefined && (
                  <progress
                    className={`plan-usage__bar${overQuota ? ' plan-usage__bar--over' : ''}`}
                    value={Math.min(monthlyUsage, developer.monthlyQuota)}
                    max={Math.max(developer.monthlyQuota, 1)}
                  />
                )}
              </div>
            </>
          ) : (
            <p className="notice">利用状況なし（開発者登録なし）</p>
          )}
        </section>

        <section>
          <h4 style={{ margin: '0 0 8px' }}>ロール</h4>
          <p>
            現在: <RoleBadges roles={user.roles} />
            {user.roles.length === 0 && '—'}
          </p>

          {isSelf && (
            <div className="notice" style={{ marginBottom: 12 }}>
              自分自身のロールは変更できません。
            </div>
          )}

          <div className="radio-group">
            {ROLES.map((role) => {
              const meta = ROLE_META[role];
              return (
                <label key={role} className="radio">
                  <input
                    type="checkbox"
                    disabled={!meta.editable || isSelf}
                    checked={draftRoles.includes(role)}
                    onChange={() => toggleRole(role)}
                  />
                  <span>
                    {meta.label} — {meta.description}
                  </span>
                </label>
              );
            })}
          </div>

          {error && (
            <div className="viewer__error" style={{ marginTop: 12 }}>
              {error}
            </div>
          )}

          <div className="modal__actions">
            <button type="button" className="btn" onClick={onClose}>
              閉じる
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={!dirty || saving || isSelf}
              onClick={save}
            >
              {saving ? '保存中…' : '保存'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

const BAN_REASON_MAX = 200;

/**
 * BAN確認モーダル（理由メモを任意入力）。
 * 実装は ReportModal.tsx を踏襲（Escape/背景クリックで閉じる・body スクロールロック等）。
 */
function BanConfirmModal({
  open,
  onCancel,
  onConfirm,
  confirming,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  confirming: boolean;
}) {
  const [reason, setReason] = useState('');
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  // 開き直すたびに入力をリセットする。
  useEffect(() => {
    if (!open) return;
    setReason('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  useEffect(() => {
    if (!open) return;
    confirmBtnRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="ban-confirm-title">
      <div className="modal__backdrop" onClick={onCancel} />
      <div className="modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 id="ban-confirm-title" className="modal__title">
            このユーザーを無効化しますか？
          </h3>
          <button type="button" className="modal__close" onClick={onCancel} aria-label="閉じる">
            ×
          </button>
        </div>
        <div className="form">
          <div className="field">
            <label htmlFor="ban-reason">
              理由（任意） ({reason.length}/{BAN_REASON_MAX})
            </label>
            <textarea
              id="ban-reason"
              rows={3}
              maxLength={BAN_REASON_MAX}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={confirming}
            />
          </div>
          <div className="modal__actions">
            <button type="button" className="btn" onClick={onCancel} disabled={confirming}>
              キャンセル
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => onConfirm(reason)}
              disabled={confirming}
              ref={confirmBtnRef}
            >
              {confirming ? '処理中…' : '無効化する'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UsersTab({
  onFilterAnimations,
}: {
  onFilterAnimations: (sub: string, label: string) => void;
}) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [developers, setDevelopers] = useState<DeveloperInfo[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [busySub, setBusySub] = useState<string | null>(null);
  // 当月の合計リクエスト数（sub -> requests）。詳細モーダルを開いた時に一度だけ取得し、以降は使い回す。
  const [usageBySub, setUsageBySub] = useState<Record<string, number> | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [detailsSub, setDetailsSub] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [activeEmail, setActiveEmail] = useState('');
  // BAN確認モーダルの対象 sub（null なら非表示）。
  const [banTarget, setBanTarget] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    Promise.all([fetchAdminUsers(undefined, activeEmail || undefined), fetchAdminDevelopers()])
      .then(([res, devs]) => {
        setUsers(res.users);
        setNextToken(res.paginationToken);
        setDevelopers(devs);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [activeEmail]);

  const developerBySub = useMemo(() => {
    const map = new Map<string, DeveloperInfo>();
    for (const dev of developers) map.set(dev.sub, dev);
    return map;
  }, [developers]);

  const { page, setPage, totalPages, pageItems } = usePagination(users, PAGE_SIZE);
  // nextToken が残っている間は「まだ未取得のページがあるかもしれない」ことを表すため、
  // 表示上の総ページ数を1つ多く見せる。次へ押下時にそのページへ入ったら実際に追加取得する。
  const effectiveTotalPages = nextToken ? totalPages + 1 : totalPages;

  // 検索条件が変わったら、ページ範囲外になる前に明示的に1ページ目へ戻す。
  useEffect(() => {
    setPage(1);
  }, [activeEmail, setPage]);

  const loadMore = async () => {
    if (!nextToken) return;
    setLoadingMore(true);
    try {
      const res = await fetchAdminUsers(nextToken, activeEmail || undefined);
      setUsers((prev) => [...prev, ...res.users]);
      setNextToken(res.paginationToken);
    } catch (e) {
      window.alert(String(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearch = () => {
    setActiveEmail(emailInput.trim());
  };

  const handlePageChange = async (nextPage: number) => {
    if (nextPage > totalPages && nextToken) {
      await loadMore();
    }
    setPage(nextPage);
  };

  const handleBanConfirm = async (reason: string) => {
    const sub = banTarget;
    if (!sub) return;
    setBusySub(sub);
    try {
      const updated = await banUser(sub, reason);
      setUsers((prev) =>
        prev.map((u) => (u.sub === sub ? { ...u, enabled: false, banReason: updated.banReason } : u)),
      );
      setBanTarget(null);
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusySub(null);
    }
  };

  // ユーザー詳細モーダルからの「バン」押下: BAN理由メモ入力の確認モーダルを開く（実行は handleBanConfirm）。
  const handleBan = (sub: string) => {
    setBanTarget(sub);
  };

  const handleUnban = async (sub: string) => {
    if (!window.confirm('このユーザーの無効化を解除しますか？')) return;
    setBusySub(sub);
    try {
      await unbanUser(sub);
      setUsers((prev) => prev.map((u) => (u.sub === sub ? { ...u, enabled: true } : u)));
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusySub(null);
    }
  };

  // 詳細モーダルを初めて開くタイミングで直近30日分の利用状況をまとめて取得し、当月分を sub ごとに集計する。
  // 何度開いても再取得はしない（usageBySub が null の間だけ実行）。
  const ensureUsageLoaded = async () => {
    if (usageBySub || usageLoading) return;
    setUsageLoading(true);
    try {
      const { usage } = await fetchAdminUsage(30);
      const currentMonth = new Date().toISOString().slice(0, 7);
      const map: Record<string, number> = {};
      for (const row of usage) {
        if (!row.date.startsWith(currentMonth)) continue;
        map[row.sub] = (map[row.sub] ?? 0) + row.requests;
      }
      setUsageBySub(map);
    } catch (e) {
      // 使用量が取れなくても詳細モーダル自体は開けるようにする（課金セクションは「読み込み中」のまま留める）。
      console.error(e);
    } finally {
      setUsageLoading(false);
    }
  };

  const openDetails = (sub: string) => {
    setDetailsSub(sub);
    void ensureUsageLoaded();
  };

  const handleRolesUpdated = (updated: { sub: string; roles: Role[] }) => {
    setUsers((prev) => prev.map((u) => (u.sub === updated.sub ? { ...u, roles: updated.roles } : u)));
  };

  const detailsUser = detailsSub ? users.find((u) => u.sub === detailsSub) : undefined;

  const searchBar = (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
      <input
        type="text"
        placeholder="email で検索（前方一致）"
        value={emailInput}
        onChange={(e) => setEmailInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSearch();
        }}
        style={{ width: 280 }}
      />
      <button className="btn btn--primary" onClick={handleSearch}>
        検索
      </button>
    </div>
  );

  if (loading) {
    return (
      <>
        {searchBar}
        <Spinner />
      </>
    );
  }
  if (error) {
    return (
      <>
        {searchBar}
        <p className="viewer__error">{error}</p>
      </>
    );
  }
  if (users.length === 0) {
    return (
      <>
        {searchBar}
        <p>{activeEmail ? '該当するユーザーが見つかりません。' : 'ユーザーがいません。'}</p>
      </>
    );
  }

  return (
    <>
      {searchBar}
      <table className="table">
        <thead>
          <tr>
            <th>作成日</th>
            <th>email</th>
            <th>表示名</th>
            <th>ステータス</th>
            <th>有効</th>
            <th>ロール</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((u) => {
            return (
              <tr key={u.sub}>
                <td>{u.createdAt ? new Date(u.createdAt).toLocaleString('ja-JP') : '—'}</td>
                <td>{u.email ?? '—'}</td>
                <td>
                  <UserAvatarChip iconUrl={u.iconUrl} name={u.nickname ?? displayNameFallback(u.email, u.sub)} />
                </td>
                <td>{u.status}</td>
                <td>
                  {u.enabled ? (
                    <span className="badge badge--approved">有効</span>
                  ) : (
                    <span className="badge badge--rejected">無効</span>
                  )}
                </td>
                <td>
                  <RoleBadges roles={u.roles} />
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {u.enabled ? (
                      <button className="btn" disabled={busySub === u.sub} onClick={() => setBanTarget(u.sub)}>
                        バン
                      </button>
                    ) : (
                      <button className="btn" disabled={busySub === u.sub} onClick={() => handleUnban(u.sub)}>
                        バン解除
                      </button>
                    )}
                    <button className="btn" onClick={() => openDetails(u.sub)}>
                      詳細
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        page={page}
        totalPages={effectiveTotalPages}
        onChange={handlePageChange}
        disableNext={loadingMore}
      />
      {loadingMore && (
        <div style={{ marginTop: 8 }}>
          <Spinner inline size="sm" />
        </div>
      )}

      {detailsUser && (
        <UserDetailsModal
          open={!!detailsSub}
          onClose={() => setDetailsSub(null)}
          user={detailsUser}
          developer={developerBySub.get(detailsUser.sub)}
          monthlyUsage={usageBySub ? usageBySub[detailsUser.sub] ?? 0 : undefined}
          currentUserSub={currentUser?.sub}
          onRolesUpdated={handleRolesUpdated}
          onFilterAnimations={onFilterAnimations}
          onBan={handleBan}
          onUnban={handleUnban}
          banBusy={busySub === detailsUser.sub}
        />
      )}

      <BanConfirmModal
        open={!!banTarget}
        onCancel={() => setBanTarget(null)}
        onConfirm={handleBanConfirm}
        confirming={!!banTarget && busySub === banTarget}
      />
    </>
  );
}

function TagsTab() {
  const [tags, setTags] = useState<AdminTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [busyName, setBusyName] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editNameDraft, setEditNameDraft] = useState('');
  const [editDescDraft, setEditDescDraft] = useState('');
  // リネームは影響が大きいため、1回目の保存クリックでは確認表示だけ出し、2回目のクリックで実際に保存する。
  const [renameConfirming, setRenameConfirming] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const { page, setPage, totalPages, pageItems } = usePagination(tags, PAGE_SIZE);

  const loadTags = () => {
    setLoading(true);
    return fetchAdminTags()
      .then(setTags)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await createAdminTag({ name, description: description || undefined });
      setName('');
      setDescription('');
      await loadTags();
    } catch (e) {
      window.alert(String(e));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (target: AdminTag) => {
    if (!window.confirm(`タグ「${target.name}」を削除しますか？（既存のアニメーションから同名タグは消えません）`)) return;
    setBusyName(target.name);
    try {
      await deleteAdminTag(target.name);
      setTags((prev) => prev.filter((t) => t.name !== target.name));
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusyName(null);
    }
  };

  const startEdit = (tag: AdminTag) => {
    setEditingName(tag.name);
    setEditNameDraft(tag.name);
    setEditDescDraft(tag.description ?? '');
    setRenameConfirming(false);
  };

  const cancelEdit = () => {
    setEditingName(null);
    setEditNameDraft('');
    setEditDescDraft('');
    setRenameConfirming(false);
  };

  const saveEdit = async (tag: AdminTag) => {
    const newName = editNameDraft.trim();
    const renaming = newName !== '' && newName !== tag.name;

    if (renaming && newName === OFFICIAL_TAG) {
      window.alert('「公式サンプル」タグへの変更はできません（システムが判定に使用しているため）');
      return;
    }

    // リネームする場合は、1回目のクリックでは確認表示のみ出して実際の保存を行わない。
    if (renaming && !renameConfirming) {
      setRenameConfirming(true);
      return;
    }

    setSavingEdit(true);
    try {
      const patch: { description?: string; newName?: string } = {};
      // 未登録タグ（タグマスタが無い）には説明が無いため、説明は送らない。
      if (!tag.unregistered) patch.description = editDescDraft;
      if (renaming) patch.newName = newName;

      await updateAdminTag(tag.name, patch);
      cancelEdit();
      // リネームは利用数や他タグとの統合にも影響するため、一覧を取り直して整合させる。
      if (renaming) {
        await loadTags();
      } else {
        setTags((prev) =>
          prev.map((t) => (t.name === tag.name ? { ...t, description: editDescDraft || undefined } : t)),
        );
      }
    } catch (e) {
      window.alert(String(e));
      setRenameConfirming(false);
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <p className="viewer__error">{error}</p>;

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
        <input
          type="text"
          placeholder="タグ名（必須・30文字以内）"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={30}
          style={{ width: 200 }}
        />
        <input
          type="text"
          placeholder="説明（任意・200文字以内）"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={200}
          style={{ width: 320 }}
        />
        <button className="btn btn--primary" disabled={creating || !name.trim()} onClick={handleCreate}>
          追加
        </button>
      </div>

      {tags.length === 0 ? (
        <p>登録済みのタグはまだありません。</p>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>作成日</th>
                <th>名前</th>
                <th>説明</th>
                <th>利用数</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((tag) => {
                const editing = editingName === tag.name;
                const isOfficial = tag.name === OFFICIAL_TAG;
                const trimmedDraft = editNameDraft.trim();
                const willRename = editing && trimmedDraft !== '' && trimmedDraft !== tag.name;
                const willMerge = willRename && tags.some((t) => t.name === trimmedDraft);
                return (
                  <tr key={tag.name}>
                    <td>{tag.createdAt ? new Date(tag.createdAt).toLocaleString('ja-JP') : '—'}</td>
                    <td>
                      {editing ? (
                        <input
                          type="text"
                          value={editNameDraft}
                          onChange={(e) => {
                            setEditNameDraft(e.target.value);
                            setRenameConfirming(false);
                          }}
                          maxLength={30}
                          style={{ width: 160 }}
                        />
                      ) : (
                        <>
                          {tag.name}
                          {tag.unregistered && (
                            <span className="badge badge--pending" style={{ marginLeft: 6 }}>
                              未登録
                            </span>
                          )}
                          {isOfficial && (
                            <span className="badge" style={{ marginLeft: 6 }}>
                              公式サンプル
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {editing ? (
                        tag.unregistered ? (
                          <span style={{ color: 'var(--muted, #888)' }}>未登録タグのため説明はありません</span>
                        ) : (
                          <input
                            type="text"
                            value={editDescDraft}
                            onChange={(e) => setEditDescDraft(e.target.value)}
                            maxLength={200}
                            style={{ width: 280 }}
                          />
                        )
                      ) : (
                        (tag.description ?? '—')
                      )}
                    </td>
                    <td>{tag.usageCount ?? 0}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {editing ? (
                          <>
                            {willRename && renameConfirming && (
                              <p style={{ width: '100%', fontSize: 13, color: '#b45309', margin: '0 0 4px' }}>
                                タグ名を変更すると、使用中の {tag.usageCount ?? 0} 件のアニメーションも書き換わります。
                                {willMerge ? `既存タグ「${trimmedDraft}」へ統合されます。` : ''}よろしいですか？
                              </p>
                            )}
                            <button
                              className="btn btn--primary"
                              disabled={savingEdit}
                              onClick={() => saveEdit(tag)}
                            >
                              {willRename && renameConfirming ? '変更を確定' : '保存'}
                            </button>
                            <button className="btn" disabled={savingEdit} onClick={cancelEdit}>
                              キャンセル
                            </button>
                          </>
                        ) : isOfficial ? null : (
                          <>
                            <button className="btn" onClick={() => startEdit(tag)}>
                              編集
                            </button>
                            {!tag.unregistered && (
                              <button
                                className="btn"
                                disabled={busyName === tag.name}
                                onClick={() => handleDelete(tag)}
                              >
                                削除
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </>
  );
}

function CategoriesTab() {
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [busyName, setBusyName] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const { page, setPage, totalPages, pageItems } = usePagination(categories, PAGE_SIZE);

  useEffect(() => {
    setLoading(true);
    fetchAdminCategories()
      .then(setCategories)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const category = await createAdminCategory({ name, description: description || undefined });
      setCategories((prev) => [category, ...prev]);
      setName('');
      setDescription('');
    } catch (e) {
      window.alert(String(e));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (target: AdminCategory) => {
    if (!window.confirm(`カテゴリ「${target.name}」を削除しますか？（既存のアニメーションからは外れません。該当のアニメーションは「未分類」表示になります）`)) return;
    setBusyName(target.name);
    try {
      await deleteAdminCategory(target.name);
      setCategories((prev) => prev.filter((c) => c.name !== target.name));
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusyName(null);
    }
  };

  const startEdit = (category: AdminCategory) => {
    setEditingName(category.name);
    setEditDraft(category.description ?? '');
  };

  const cancelEdit = () => {
    setEditingName(null);
    setEditDraft('');
  };

  const saveEdit = async (name: string) => {
    setSavingEdit(true);
    try {
      const updated = await updateAdminCategory(name, { description: editDraft });
      setCategories((prev) => prev.map((c) => (c.name === name ? updated : c)));
      setEditingName(null);
      setEditDraft('');
    } catch (e) {
      window.alert(String(e));
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <p className="viewer__error">{error}</p>;

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
        <input
          type="text"
          placeholder="カテゴリ名（必須・30文字以内）"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={30}
          style={{ width: 200 }}
        />
        <input
          type="text"
          placeholder="説明（任意・200文字以内）"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={200}
          style={{ width: 320 }}
        />
        <button className="btn btn--primary" disabled={creating || !name.trim()} onClick={handleCreate}>
          追加
        </button>
      </div>

      {categories.length === 0 ? (
        <p>登録済みのカテゴリはまだありません。</p>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>作成日</th>
                <th>名前</th>
                <th>説明</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((category) => {
                const editing = editingName === category.name;
                return (
                  <tr key={category.name}>
                    <td>{new Date(category.createdAt).toLocaleString('ja-JP')}</td>
                    <td>{category.name}</td>
                    <td>
                      {editing ? (
                        <input
                          type="text"
                          value={editDraft}
                          onChange={(e) => setEditDraft(e.target.value)}
                          maxLength={200}
                          style={{ width: 280 }}
                        />
                      ) : (
                        (category.description ?? '—')
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {editing ? (
                          <>
                            <button
                              className="btn btn--primary"
                              disabled={savingEdit}
                              onClick={() => saveEdit(category.name)}
                            >
                              保存
                            </button>
                            <button className="btn" disabled={savingEdit} onClick={cancelEdit}>
                              キャンセル
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="btn" onClick={() => startEdit(category)}>
                              編集
                            </button>
                            <button
                              className="btn"
                              disabled={busyName === category.name}
                              onClick={() => handleDelete(category)}
                            >
                              削除
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </>
  );
}

// packageName + version の組でバージョンを一意に特定するキー（行の React key / 操作中判定に使う）。
const vpmVersionKey = (v: AdminVpmVersion) => `${v.packageName}#${v.version}`;

function VpmTab() {
  const [versions, setVersions] = useState<AdminVpmVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchAdminVpmVersions()
      .then(setVersions)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const handleSuspend = async (v: AdminVpmVersion) => {
    setBusyKey(vpmVersionKey(v));
    try {
      const updated = await updateAdminVpmVersion(v.packageName, v.version, 'suspended');
      setVersions((prev) => prev.map((x) => (vpmVersionKey(x) === vpmVersionKey(v) ? updated : x)));
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const handleResume = async (v: AdminVpmVersion) => {
    setBusyKey(vpmVersionKey(v));
    try {
      const updated = await updateAdminVpmVersion(v.packageName, v.version, 'published');
      setVersions((prev) => prev.map((x) => (vpmVersionKey(x) === vpmVersionKey(v) ? updated : x)));
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const handleDelete = async (v: AdminVpmVersion) => {
    if (!window.confirm(`バージョン ${v.version} を削除しますか？\nzip ファイルも S3 から削除され、元に戻せません。`)) return;
    setBusyKey(vpmVersionKey(v));
    try {
      await deleteAdminVpmVersion(v.packageName, v.version);
      setVersions((prev) => prev.filter((x) => vpmVersionKey(x) !== vpmVersionKey(v)));
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusyKey(null);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <p className="viewer__error">{error}</p>;
  if (versions.length === 0) return <p>登録済みのバージョンはまだありません。</p>;

  // パッケージごとにグループ化して表示する（複数パッケージ配信に伴い、どのバージョンがどの
  // パッケージのものか一目で分かるようにするため）。versions は新着順で返ってくるので、
  // Map の挿入順（＝最初に登場したパッケージの順）がそのままグループの表示順になる。
  const grouped = new Map<string, AdminVpmVersion[]>();
  for (const v of versions) {
    const list = grouped.get(v.packageName);
    if (list) list.push(v);
    else grouped.set(v.packageName, [v]);
  }

  return (
    <>
      {[...grouped.entries()].map(([packageName, pkgVersions]) => (
        <VpmPackageSection
          key={packageName}
          packageName={packageName}
          versions={pkgVersions}
          busyKey={busyKey}
          onSuspend={handleSuspend}
          onResume={handleResume}
          onDelete={handleDelete}
        />
      ))}
    </>
  );
}

function VpmPackageSection({
  packageName,
  versions,
  busyKey,
  onSuspend,
  onResume,
  onDelete,
}: {
  packageName: string;
  versions: AdminVpmVersion[];
  busyKey: string | null;
  onSuspend: (v: AdminVpmVersion) => void;
  onResume: (v: AdminVpmVersion) => void;
  onDelete: (v: AdminVpmVersion) => void;
}) {
  const { page, setPage, totalPages, pageItems } = usePagination(versions, PAGE_SIZE);

  return (
    <div style={{ marginBottom: 24 }}>
      <h3>{packageName}</h3>
      <table className="table">
        <thead>
          <tr>
            <th>バージョン</th>
            <th>登録日時</th>
            <th>状態</th>
            <th>SHA256</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((v) => {
            const key = vpmVersionKey(v);
            return (
              <tr key={key}>
                <td>{v.version}</td>
                <td>{new Date(v.createdAt).toLocaleString('ja-JP')}</td>
                <td>
                  <span className={v.status === 'published' ? 'badge badge--approved' : 'badge badge--suspended'}>
                    {v.status === 'published' ? '配信中' : '停止中'}
                  </span>
                </td>
                <td title={v.zipSHA256}>{v.zipSHA256.slice(0, 12)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {v.status === 'published' ? (
                      <button className="btn" disabled={busyKey === key} onClick={() => onSuspend(v)}>
                        配信停止
                      </button>
                    ) : (
                      <>
                        <button className="btn" disabled={busyKey === key} onClick={() => onResume(v)}>
                          配信再開
                        </button>
                        <button className="btn" disabled={busyKey === key} onClick={() => onDelete(v)}>
                          削除
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}

const CONTACT_CATEGORY_LABEL: Record<ContactCategory, string> = {
  general: '一般',
  rights: '権利関係',
  privacy: 'プライバシー',
  bug: '不具合',
  other: 'その他',
};

/** 本文プレビューの最大文字数（これを超えたら省略表示し、クリックで全文展開する）。 */
const MESSAGE_PREVIEW_LENGTH = 40;

const REPORT_CATEGORY_LABEL: Record<ReportCategory, string> = {
  rights: '権利侵害',
  inappropriate: '不適切な内容',
  spam: 'スパム',
  other: 'その他',
};

function ReportsTab() {
  const [items, setItems] = useState<AdminReport[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchAdminReports()
      .then((res) => {
        setItems(res.items);
        setCursor(res.cursor);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await fetchAdminReports(cursor);
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.cursor);
    } catch (e) {
      window.alert(String(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleToggleStatus = async (item: AdminReport) => {
    const next = item.status === 'open' ? 'closed' : 'open';
    setBusyId(item.id);
    try {
      const updated = await updateAdminReport(item.id, { status: next });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: updated.status } : i)));
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <p>読み込み中…</p>;
  if (error) return <p className="viewer__error">{error}</p>;
  if (items.length === 0) return <p>通報はまだありません。</p>;

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>通報日時</th>
            <th>対象アニメーション</th>
            <th>カテゴリ</th>
            <th>詳細</th>
            <th>ステータス</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => {
            const expanded = expandedId === r.id;
            const detail = r.detail ?? '';
            const needsTruncation = detail.length > MESSAGE_PREVIEW_LENGTH;
            const preview = expanded || !needsTruncation ? detail : `${detail.slice(0, MESSAGE_PREVIEW_LENGTH)}…`;
            return (
              <tr key={r.id}>
                <td>{new Date(r.createdAt).toLocaleString('ja-JP')}</td>
                <td>
                  <Link to={`/animations/${r.animationId}`}>{r.animationTitle}</Link>
                </td>
                <td>{REPORT_CATEGORY_LABEL[r.category]}</td>
                <td
                  style={{
                    whiteSpace: 'pre-wrap',
                    maxWidth: 320,
                    cursor: needsTruncation ? 'pointer' : 'default',
                  }}
                  onClick={() => needsTruncation && setExpandedId(expanded ? null : r.id)}
                >
                  {preview || '—'}
                </td>
                <td>
                  <span className={r.status === 'open' ? 'badge badge--pending' : 'badge badge--approved'}>
                    {r.status === 'open' ? '未対応' : '対応済み'}
                  </span>
                </td>
                <td>
                  <button className="btn" disabled={busyId === r.id} onClick={() => handleToggleStatus(r)}>
                    {r.status === 'open' ? '対応済みにする' : '未対応に戻す'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {cursor && (
        <div style={{ marginTop: 12 }}>
          <button className="btn" disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? '読み込み中…' : 'さらに読み込む'}
          </button>
        </div>
      )}
    </>
  );
}

function SimilarityFlagsTab() {
  const [items, setItems] = useState<AdminSimilarityFlag[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchAdminSimilarityFlags()
      .then((res) => {
        setItems(res.items);
        setCursor(res.cursor);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await fetchAdminSimilarityFlags(cursor);
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.cursor);
    } catch (e) {
      window.alert(String(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleToggleStatus = async (item: AdminSimilarityFlag) => {
    const next = item.status === 'open' ? 'closed' : 'open';
    setBusyId(item.id);
    try {
      const updated = await updateAdminSimilarityFlag(item.id, { status: next });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: updated.status } : i)));
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <p>読み込み中…</p>;
  if (error) return <p className="viewer__error">{error}</p>;
  if (items.length === 0) return <p>類似検出はまだありません。</p>;

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>検出日時</th>
            <th>投稿</th>
            <th>類似先（削除済み）</th>
            <th>類似度</th>
            <th>ステータス</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((f) => (
            <tr key={f.id}>
              <td>{new Date(f.createdAt).toLocaleString('ja-JP')}</td>
              <td>
                <Link to={`/animations/${f.animationId}`}>{f.animationTitle}</Link>
              </td>
              <td>
                {f.blocklistTitle}
                <div style={{ fontSize: 12, opacity: 0.7 }}>{f.blocklistAnimationId}</div>
              </td>
              <td>
                {(f.similarity * 100).toFixed(1)}%
                {f.mirrored && (
                  <div>
                    <span className="badge badge--pending">ミラー</span>
                  </div>
                )}
                {f.shadowWouldHaveBlocked && (
                  <div>
                    <span className="badge badge--pending">shadow時ブロック相当</span>
                  </div>
                )}
              </td>
              <td>
                <span className={f.status === 'open' ? 'badge badge--pending' : 'badge badge--approved'}>
                  {f.status === 'open' ? '未対応' : '対応済み'}
                </span>
              </td>
              <td>
                <button className="btn" disabled={busyId === f.id} onClick={() => handleToggleStatus(f)}>
                  {f.status === 'open' ? '対応済みにする' : '未対応に戻す'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {cursor && (
        <div style={{ marginTop: 12 }}>
          <button className="btn" disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? '読み込み中…' : 'さらに読み込む'}
          </button>
        </div>
      )}
    </>
  );
}

function ContactsTab() {
  const [items, setItems] = useState<AdminContact[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchAdminContacts()
      .then((res) => {
        setItems(res.items);
        setCursor(res.cursor);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await fetchAdminContacts(cursor);
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.cursor);
    } catch (e) {
      window.alert(String(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleToggleStatus = async (item: AdminContact) => {
    const next = item.status === 'open' ? 'closed' : 'open';
    setBusyId(item.id);
    try {
      const updated = await updateAdminContact(item.id, { status: next });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: updated.status } : i)));
    } catch (e) {
      window.alert(String(e));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <p className="viewer__error">{error}</p>;
  if (items.length === 0) return <p>問い合わせはまだありません。</p>;

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>受信日時</th>
            <th>カテゴリ</th>
            <th>名前</th>
            <th>返信用メール</th>
            <th>本文</th>
            <th>ステータス</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => {
            const expanded = expandedId === c.id;
            const needsTruncation = c.message.length > MESSAGE_PREVIEW_LENGTH;
            const preview = expanded || !needsTruncation ? c.message : `${c.message.slice(0, MESSAGE_PREVIEW_LENGTH)}…`;
            return (
              <tr key={c.id}>
                <td>{new Date(c.createdAt).toLocaleString('ja-JP')}</td>
                <td>{CONTACT_CATEGORY_LABEL[c.category]}</td>
                <td>{c.name || '—'}</td>
                <td>
                  <a href={`mailto:${c.email}`}>{c.email}</a>
                </td>
                <td
                  style={{
                    whiteSpace: 'pre-wrap',
                    maxWidth: 320,
                    cursor: needsTruncation ? 'pointer' : 'default',
                  }}
                  onClick={() => needsTruncation && setExpandedId(expanded ? null : c.id)}
                >
                  {preview}
                </td>
                <td>
                  <span className={c.status === 'open' ? 'badge badge--pending' : 'badge badge--approved'}>
                    {c.status === 'open' ? '未対応' : '対応済み'}
                  </span>
                </td>
                <td>
                  <button className="btn" disabled={busyId === c.id} onClick={() => handleToggleStatus(c)}>
                    {c.status === 'open' ? '対応済みにする' : '未対応に戻す'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {cursor && (
        <div style={{ marginTop: 12 }}>
          <button className="btn" disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? <Spinner inline size="sm" /> : 'さらに読み込む'}
          </button>
        </div>
      )}
    </>
  );
}

/** 監査ログの action の日本語ラベル。未知の action はそのまま表示する。 */
const AUDIT_ACTION_LABEL: Record<string, string> = {
  update_developer: '開発者情報を更新',
  delete_animation: 'アニメーションを削除',
  update_animation_visibility: '公開設定を変更',
  ban_user: 'ユーザーをBAN',
  unban_user: 'ユーザーのBANを解除',
  update_user_roles: 'ロールを変更',
  create_tag: 'タグを作成',
  delete_tag: 'タグを削除',
  update_tag: 'タグを更新',
  update_contact_status: '問い合わせステータスを変更',
  update_report_status: '通報ステータスを変更',
};

function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABEL[action] ?? action;
}

function AuditLogsTab() {
  const [items, setItems] = useState<AdminAuditLog[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    fetchAdminAuditLogs()
      .then((res) => {
        setItems(res.items);
        setCursor(res.cursor);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await fetchAdminAuditLogs(cursor);
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.cursor);
    } catch (e) {
      window.alert(String(e));
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) return <p>読み込み中…</p>;
  if (error) return <p className="viewer__error">{error}</p>;
  if (items.length === 0) return <p>監査ログはまだありません。</p>;

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>日時</th>
            <th>操作者</th>
            <th>操作</th>
            <th>対象</th>
            <th>詳細</th>
          </tr>
        </thead>
        <tbody>
          {items.map((log) => (
            <tr key={log.id}>
              <td>{new Date(log.createdAt).toLocaleString('ja-JP')}</td>
              <td>{log.actorEmail ?? log.actorSub}</td>
              <td>{auditActionLabel(log.action)}</td>
              <td>
                {log.targetType === 'animation' ? (
                  <Link to={`/animations/${log.targetId}`}>
                    {log.targetType}:{log.targetId}
                  </Link>
                ) : (
                  `${log.targetType}:${log.targetId}`
                )}
              </td>
              <td style={{ whiteSpace: 'pre-wrap', maxWidth: 320 }}>{log.detail || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {cursor && (
        <div style={{ marginTop: 12 }}>
          <button className="btn" disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? '読み込み中…' : 'さらに読み込む'}
          </button>
        </div>
      )}
    </>
  );
}

const POSE_SERVER_STATE_LABEL: Record<string, string> = {
  running: '稼働中',
  starting: '準備中（モデル読み込みに10〜15分ほどかかることがあります）',
  stopping: '停止中…',
  stopped: '停止',
  unhealthy: '異常あり',
  unreachable: '接続不可（PC・トンネル未稼働）',
  unconfigured: '未設定',
};

const POSE_SERVER_BADGE_CLASS: Record<string, string> = {
  running: 'badge badge--approved',
  starting: 'badge badge--pending',
  stopping: 'badge badge--pending',
  stopped: 'badge badge--suspended',
  unhealthy: 'badge badge--rejected',
  unreachable: 'badge badge--rejected',
  unconfigured: 'badge badge--suspended',
};

/**
 * サーバー状態のラベル。stopped は受付ON/OFFで意味が変わるため出し分ける
 * （受付ONの stopped は「画像が届けば自動起動する待機状態」であり、受付OFFの stopped とは違い
 * 単なる「停止」ではないため）。
 */
function poseServerStateLabel(state: string, accepting: boolean): string {
  if (state === 'stopped' && accepting) return '待機中（画像が届いたら自動起動）';
  return POSE_SERVER_STATE_LABEL[state] ?? state;
}

/** pose-server の状態表示のポーリング間隔。 */
const POSE_SERVER_POLL_INTERVAL_MS = 10_000;

/**
 * pose-factory（ポーズ量産ラインの選別UI/3D目視QA）のベース URL。末尾スラッシュの有無に
 * かかわらず動くよう正規化しておく。未設定ならリンクのセクション自体を表示しない。
 */
const POSE_FACTORY_BASE_URL = (import.meta.env.VITE_POSE_FACTORY_URL ?? '').replace(/\/+$/, '');

function PoseServerTab() {
  const [state, setState] = useState('unconfigured');
  const [accepting, setAccepting] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false); // 起動/停止（手動操作）中
  const [togglingAccepting, setTogglingAccepting] = useState(false); // 受付ON/OFF切り替え中

  // ポーリングと各操作（受付トグル・手動起動/停止）のレスポンスは非同期に返ってくるため、
  // 後から発行したリクエストの結果を常に優先する。連番を振り、自分より新しいリクエストが
  // 既に発行済みなら反映をスキップすることで、例えば「受付トグル直後にポーリングが返ってきて
  // 古い accepting で表示が巻き戻る」といった競合を防ぐ。
  const requestSeqRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      const seq = ++requestSeqRef.current;
      return fetchPoseServerHealth()
        .then((res) => {
          if (cancelled || seq !== requestSeqRef.current) return;
          setState(res.state);
          setAccepting(res.accepting);
          setError('');
        })
        .catch((e) => {
          if (cancelled || seq !== requestSeqRef.current) return;
          setError(String(e));
        });
    };

    setLoading(true);
    refresh().finally(() => {
      if (!cancelled) setLoading(false);
    });
    const timer = setInterval(refresh, POSE_SERVER_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const handleStart = async () => {
    const seq = ++requestSeqRef.current;
    setBusy(true);
    try {
      const res = await startAdminPoseServer();
      if (seq === requestSeqRef.current) {
        setState(res.state);
        setError('');
      }
    } catch (e) {
      if (seq === requestSeqRef.current) setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async () => {
    const seq = ++requestSeqRef.current;
    setBusy(true);
    try {
      const res = await stopAdminPoseServer();
      if (seq === requestSeqRef.current) {
        setState(res.state);
        setError('');
      }
    } catch (e) {
      if (seq === requestSeqRef.current) setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleToggleAccepting = async (next: boolean) => {
    const seq = ++requestSeqRef.current;
    setTogglingAccepting(true);
    try {
      const res = await setAdminPoseAccepting(next);
      if (seq === requestSeqRef.current) {
        setAccepting(res.accepting);
        setState(res.state);
        setError('');
      }
    } catch (e) {
      if (seq === requestSeqRef.current) setError(String(e));
    } finally {
      setTogglingAccepting(false);
    }
  };

  if (loading) return <Spinner />;

  const canStart = state === 'stopped' || state === 'unhealthy';
  const canStop = state === 'running' || state === 'starting' || state === 'unhealthy';
  const unavailable = state === 'unreachable' || state === 'unconfigured';

  return (
    <div>
      {/* 受付トグル: 最上位の操作。ONにしておけば利用者が画像を送るだけで自動起動・自動停止する
          ため、管理者は基本的に触らなくてよい。 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <span className={accepting ? 'badge badge--approved' : 'badge badge--rejected'}>
          受付{accepting ? 'ON' : 'OFF'}
        </span>
        <button
          type="button"
          className={accepting ? 'btn' : 'btn btn--primary'}
          disabled={unavailable || togglingAccepting}
          onClick={() => handleToggleAccepting(!accepting)}
        >
          {accepting ? '受付を停止する' : '受付を開始する'}
        </button>
        {togglingAccepting && <Spinner inline size="sm" />}
      </div>

      <p style={{ color: 'var(--color-muted)', fontSize: 14, maxWidth: 560, marginTop: 0 }}>
        受付ONの間は、利用者が画像を送ったときだけサーバーが自動で起動します（起動に1分ほどかかります）。
        生成が終わって5分間なにも来なければ自動で停止し、GPU を解放します。
        受付OFFにすると、画像が届いてもサーバーは起動しません。稼働中のサーバーはすぐに停止します。
      </p>

      {/* 現在のサーバー稼働状態は情報表示。受付トグルとは別に、実際のコンテナの状態を伝える。 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20, marginBottom: 8 }}>
        <span className={POSE_SERVER_BADGE_CLASS[state] ?? 'badge'}>{poseServerStateLabel(state, accepting)}</span>
        {(state === 'starting' || busy) && <Spinner inline size="sm" />}
      </div>

      {error && <p className="viewer__error">{error}</p>}

      <div style={{ marginTop: 20 }}>
        <p style={{ fontWeight: 600, marginBottom: 4 }}>手動操作</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <button className="btn btn--primary" disabled={!canStart || busy} onClick={handleStart}>
            起動
          </button>
          <button className="btn" disabled={!canStop || busy} onClick={handleStop}>
            停止
          </button>
        </div>
        <p style={{ color: 'var(--color-muted)', fontSize: 13, maxWidth: 560 }}>
          事前にウォームアップしておきたいとき（起動）や、今すぐ GPU を解放したいとき（停止）に使います。
          受付OFFの間に手動で起動しても、生成後5分アイドルが続けば自動で停止します。
        </p>
      </div>

      {unavailable && (
        <div className="notice" style={{ marginTop: 16, maxWidth: 560 }}>
          自宅 PC の pose-server に接続できません。PC 側で docker compose と cloudflared トンネルが起動しているか確認してください。
        </div>
      )}

      {/* pose-factory へのリンク。上の pose-server（GPU推論サーバー）の状態とは無関係に、
          自宅PC常駐の別サービスへ誘導するだけのセクションなので、状態表示・活性制御には関与しない。 */}
      {POSE_FACTORY_BASE_URL && (
        <div style={{ marginTop: 32 }}>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>ポーズ量産ライン（pose-factory）</p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <a
              className="btn"
              href={`${POSE_FACTORY_BASE_URL}/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              選別UI（S2）を開く
            </a>
            <a
              className="btn"
              href={`${POSE_FACTORY_BASE_URL}/qa.html`}
              target="_blank"
              rel="noopener noreferrer"
            >
              3D目視QA（S5）を開く
            </a>
          </div>
          <p style={{ color: 'var(--color-muted)', fontSize: 13, maxWidth: 560 }}>
            自宅 PC に常駐しているサーバーに繋がります（PC が起動していないと開けません）。
            初回アクセス時は Cloudflare Access の認証が入ります（登録済みメールアドレスにワンタイムコードが届きます）。
            ロット ID を指定せずに開くとロット一覧が表示されます。
          </p>
        </div>
      )}
    </div>
  );
}

export default function AdminConsole() {
  const { user, ready, login } = useAuth();
  const [tab, setTab] = useState<Tab>('overview');
  const [attention, setAttention] = useState<AdminAttention | null>(null);
  // ユーザータブから「投稿を絞り込む」で渡されるオーナー絞り込み条件（アニメーションタブで使う）。
  const [animationsOwnerFilter, setAnimationsOwnerFilter] = useState<AnimationsOwnerFilter | null>(null);

  // マウント時に1回だけ「要対応」件数を取得する（ポーリングはしない）。
  useEffect(() => {
    if (!user?.isAdmin) return;
    fetchAdminAttention()
      .then(setAttention)
      .catch(() => {
        // バッジ表示用の補助情報のため、失敗してもコンソール自体は使えるようにする。
      });
  }, [user?.isAdmin]);

  // ユーザー詳細から「このユーザーの投稿を管理画面で絞り込む」が押されたら、アニメーションタブへ切り替えて絞り込む。
  const handleFilterAnimationsByOwner = (sub: string, label: string) => {
    setAnimationsOwnerFilter({ sub, label });
    setTab('animations');
  };

  if (!ready) {
    return (
      <div className="container">
        <Spinner />
      </div>
    );
  }

  if (!user || !user.isAdmin) {
    return (
      <div className="container">
        <div className="section-title">
          <h2>管理者コンソール</h2>
        </div>
        <div className="notice" style={{ marginBottom: 20 }}>
          このページを表示する権限がありません。
        </div>
        {!user && (
          <button className="btn btn--primary" onClick={login}>
            ログイン
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="container">
      <div className="section-title">
        <h2>管理者コンソール</h2>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button
          className={`btn${tab === 'overview' ? ' btn--primary' : ''}`}
          onClick={() => setTab('overview')}
        >
          概要
        </button>
        <button
          className={`btn${tab === 'developers' ? ' btn--primary' : ''}`}
          onClick={() => setTab('developers')}
        >
          開発者申請
          <TabCountBadge count={attention?.pendingDevelopers ?? 0} />
        </button>
        <button
          className={`btn${tab === 'listings' ? ' btn--primary' : ''}`}
          onClick={() => setTab('listings')}
        >
          連携アプリ
        </button>
        <button
          className={`btn${tab === 'usage' ? ' btn--primary' : ''}`}
          onClick={() => setTab('usage')}
        >
          利用状況
        </button>
        <button
          className={`btn${tab === 'animations' ? ' btn--primary' : ''}`}
          onClick={() => setTab('animations')}
        >
          アニメーション
        </button>
        <button
          className={`btn${tab === 'users' ? ' btn--primary' : ''}`}
          onClick={() => setTab('users')}
        >
          ユーザー
        </button>
        <button
          className={`btn${tab === 'tags' ? ' btn--primary' : ''}`}
          onClick={() => setTab('tags')}
        >
          タグ
        </button>
        <button
          className={`btn${tab === 'categories' ? ' btn--primary' : ''}`}
          onClick={() => setTab('categories')}
        >
          カテゴリ
        </button>
        <button
          className={`btn${tab === 'vpm' ? ' btn--primary' : ''}`}
          onClick={() => setTab('vpm')}
        >
          Unityパッケージ
        </button>
        <button
          className={`btn${tab === 'reports' ? ' btn--primary' : ''}`}
          onClick={() => setTab('reports')}
        >
          通報
          <TabCountBadge count={attention?.openReports ?? 0} />
        </button>
        <button
          className={`btn${tab === 'similarity' ? ' btn--primary' : ''}`}
          onClick={() => setTab('similarity')}
        >
          類似検出
          <TabCountBadge count={attention?.openSimilarityFlags ?? 0} />
        </button>
        <button
          className={`btn${tab === 'contacts' ? ' btn--primary' : ''}`}
          onClick={() => setTab('contacts')}
        >
          問い合わせ
          <TabCountBadge count={attention?.openContacts ?? 0} />
        </button>
        <button
          className={`btn${tab === 'audit' ? ' btn--primary' : ''}`}
          onClick={() => setTab('audit')}
        >
          監査ログ
        </button>
        <button
          className={`btn${tab === 'pose' ? ' btn--primary' : ''}`}
          onClick={() => setTab('pose')}
        >
          ポーズサーバー
        </button>
      </div>

      {tab === 'overview' && <OverviewTab attention={attention} onNavigate={setTab} />}
      {tab === 'developers' && <DevelopersTab />}
      {tab === 'listings' && <ListingsTab />}
      {tab === 'usage' && <UsageTab />}
      {tab === 'animations' && (
        <AnimationsTab
          ownerFilter={animationsOwnerFilter}
          onClearOwnerFilter={() => setAnimationsOwnerFilter(null)}
        />
      )}
      {tab === 'users' && <UsersTab onFilterAnimations={handleFilterAnimationsByOwner} />}
      {tab === 'tags' && <TagsTab />}
      {tab === 'categories' && <CategoriesTab />}
      {tab === 'vpm' && <VpmTab />}
      {tab === 'reports' && <ReportsTab />}
      {tab === 'similarity' && <SimilarityFlagsTab />}
      {tab === 'contacts' && <ContactsTab />}
      {tab === 'audit' && <AuditLogsTab />}
      {tab === 'pose' && <PoseServerTab />}
    </div>
  );
}
